var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, isValidElement } from 'react';
import classnames from 'classnames';
import { customAlphabet } from 'nanoid';
import { FormFieldContext } from './FormFieldContext';
import { ButtonToggle } from '../ButtonToggle';
import { ToggleSwitch } from '../ToggleSwitch';
import { Label } from '../Label';
import { Togglebox } from '../Togglebox';
import { Checkbox } from '../Checkbox';
import { Button } from '../Button';
import { Radio } from '../Radio';
export const FormField = React.forwardRef((_a, ref) => {
    var _b, _c;
    var { children, className, control, controlRef, controlClassName, description, disabled, el = 'div', error, label, labelProps, required, size, toggleboxControl, width, onChange } = _a, props = __rest(_a, ["children", "className", "control", "controlRef", "controlClassName", "description", "disabled", "el", "error", "label", "labelProps", "required", "size", "toggleboxControl", "width", "onChange"]);
    const controlType = (() => {
        var _a, _b, _c, _d;
        if (typeof control === 'undefined')
            return undefined;
        if (((_a = control === null || control === void 0 ? void 0 : control.displayName) === null || _a === void 0 ? void 0 : _a.toString().length) > 2)
            return (_b = control === null || control === void 0 ? void 0 : control.displayName) === null || _b === void 0 ? void 0 : _b.toString();
        if (((_c = control === null || control === void 0 ? void 0 : control.name) === null || _c === void 0 ? void 0 : _c.toString().length) > 2)
            return (_d = control === null || control === void 0 ? void 0 : control.name) === null || _d === void 0 ? void 0 : _d.toString();
        return 'control';
    })();
    const hasCounter = props.showCounter &&
        !!props.maxLength &&
        ['Input', 'TextArea', 'control'].includes(controlType);
    const [counter, setCounter] = React.useState((_c = (_b = props.defaultValue) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0);
    const passProps = () => {
        if (['Input', 'TextArea', 'control'].includes(controlType)) {
            const { showCounter } = props, rest = __rest(props, ["showCounter"]);
            return rest;
        }
        return props;
    };
    const labelFor = props.id || `formfield_id_${customAlphabet(lettersOnly, 8)()}`;
    const errMsgID = props.id || `errMsg_id_${customAlphabet(lettersOnly, 8)()}`;
    const [context, setContext] = useState({
        labelFor,
        setFormFieldContext: () => { },
    });
    const setFormFieldContext = (newContext) => setContext(Object.assign(Object.assign({}, context), newContext));
    const handleLabelClick = () => {
        var _a;
        const inputNode = (_a = context === null || context === void 0 ? void 0 : context.inputRef) === null || _a === void 0 ? void 0 : _a.current;
        if (['AnvilSelect', 'Select', 'Dropdown'].includes(controlType)) {
            inputNode === null || inputNode === void 0 ? void 0 : inputNode.focus();
            return;
        }
        inputNode === null || inputNode === void 0 ? void 0 : inputNode.click();
    };
    const handleOnChange = (e, d) => {
        var _a;
        if (hasCounter) {
            setCounter((_a = d === null || d === void 0 ? void 0 : d.value) === null || _a === void 0 ? void 0 : _a.length);
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(e, d);
    };
    const getLabel = () => {
        if (label === undefined || label === '')
            return false;
        const labelClassName = (labelProps === null || labelProps === void 0 ? void 0 : labelProps.className)
            ? labelProps.className
            : null;
        const labelClasses = classnames(labelClassName, 'FormField__label');
        return (React.createElement(Label, Object.assign({ disabled: disabled, required: required }, labelProps, { className: labelClasses }), label));
    };
    const hasDescription = (description &&
        typeof description === 'string' &&
        description.length > 0) ||
        isValidElement(description);
    const hasErrorText = (error && typeof error === 'string' && error.length > 0) ||
        isValidElement(error);
    const getControl = () => {
        const controlProps = Object.assign(Object.assign({ children,
            disabled,
            required,
            size, onChange: hasCounter ? handleOnChange : onChange, className: controlClassName }, passProps()), (control !== ToggleSwitch &&
            control !== ButtonToggle &&
            toggleboxControl !== 'toggleSwitch' &&
            control !== Button
            ? { error: typeof error !== 'undefined' ? !!error : error }
            : {}));
        // Togglebox
        if (control === Togglebox) {
            return (React.createElement(Togglebox, Object.assign({ label: label, control: toggleboxControl, "aria-errormessage": errMsgID }, controlProps)));
        }
        // Checkbox, Radio, ToggleSwitch
        if (control === Checkbox ||
            control === Radio ||
            control === ToggleSwitch) {
            return React.createElement(control, Object.assign(Object.assign({}, controlProps), { label,
                labelProps, 'aria-errormessage': errMsgID, required,
                ref }));
        }
        const createControl = control
            ? React.createElement(control, Object.assign(Object.assign({}, controlProps), { 'aria-errormessage': errMsgID, ref }))
            : children;
        return (React.createElement(React.Fragment, null,
            React.createElement("label", { className: "Field__html-label", htmlFor: context.labelFor, onClick: handleLabelClick }, getLabel()),
            React.createElement(FormFieldContext.Provider, { value: Object.assign(Object.assign({}, context), { setFormFieldContext }) }, createControl)));
    };
    const FormFieldProps = { children, disabled };
    const FormFieldClasses = classnames('FormField', className, {
        'FormField--disabled': disabled,
        'FormField--error': error,
        [`FormField--${controlType}`]: controlType,
        'FormField--small': size === 'small',
        'FormField--large': size === 'large',
        'FormField--fitted': label === undefined || label === '',
        [`FormField--width-${width}`]: width,
    });
    const FormFieldElement = el;
    return (React.createElement(FormFieldElement, Object.assign({ className: FormFieldClasses, "data-anvil-component": "FormField" }, FormFieldProps),
        React.createElement("div", { className: "FormField__wrapper" },
            getControl(),
            (hasCounter || hasErrorText || hasDescription) && (React.createElement("div", { className: "FormField__addons" },
                (hasErrorText || hasDescription) && (React.createElement("div", { className: "FormField__helpers" },
                    hasErrorText && (React.createElement("div", { className: "FormField__errorText", id: errMsgID }, error)),
                    hasDescription && (React.createElement("div", { className: "FormField__description" }, description)))),
                hasCounter && (React.createElement("div", { className: "FormField__counter" }, `${counter}/${props.maxLength}`)))))));
});
const lettersOnly = 'abcdefghijklmnopqrstuvwxyz';
//# sourceMappingURL=FormField.js.map